import { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { getTeacherClasses, getBooks } from "../services/api";
import { getUserData } from "../utils/userData";
import { buildClassJoinInstruction } from "../utils/classInvite";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import type { classDataProps, Book } from "../types/common";

export const ClassListModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [classes, setClasses] = useState<classDataProps[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedClassId, setCopiedClassId] = useState<string | null>(null);
  const { sub } = getUserData();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classesResponse, booksResponse] = await Promise.all([
          getTeacherClasses(sub),
          getBooks()
        ]);
        setClasses(classesResponse.data || []);
        setBooks(booksResponse.data || []);
      } catch (error) {
        console.error("獲取數據失敗:", error);
        setClasses([]);
        setBooks([]);
      } finally {
        setLoading(false);
      }
    };

    if (open && sub) {
      fetchData();
    }
  }, [open, sub]);

  const getBookName = (bookID: string) => {
    const book = books.find(b => b._id === bookID);
    return book?.bookDesc || bookID;
  };

  const handleCopyInviteCode = (classCode: string, classId: string) => {
    navigator.clipboard.writeText(buildClassJoinInstruction(classCode));
    setCopiedClassId(classId);
    
    // 2秒後恢復原狀
    setTimeout(() => {
      setCopiedClassId(null);
    }, 2000);
  };

  return (
    <div
      onClick={onClose}
      className={`fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-20 p-4 ${
        open ? "block" : "hidden"
      }`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white p-4 md:p-6 rounded-lg flex flex-col gap-4 max-w-[1000px] w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-center p-4 bg-primary-light text-lg font-medium text-white rounded-sm">
          班級列表
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">
            載入中...
          </div>
        ) : classes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            暫無班級資料
          </div>
        ) : (
          <>
            {/* 桌機/平板表格 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-center font-medium text-gray-900">
                      書籍
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-medium text-gray-900">
                      班級名稱
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-medium text-gray-900">
                      學生邀請碼
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-medium text-gray-900">
                      功能
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((classItem) => (
                    <tr key={classItem._id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {getBookName(classItem.bookID)}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {classItem.className}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        <span className="font-mono text-lg font-semibold">
                          {classItem.classCode}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center">
                        <button
                          onClick={() => handleCopyInviteCode(classItem.classCode, classItem._id)}
                          className="inline-flex items-center gap-1 px-3 py-2 text-primary hover:bg-primary hover:text-white rounded-md transition-colors"
                          title={copiedClassId === classItem._id ? "複製成功" : "複製邀請碼"}
                        >
                          {copiedClassId === classItem._id ? (
                            <CheckIcon fontSize="small" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                          <span className="text-sm">
                            {copiedClassId === classItem._id ? '複製成功' : '複製說明'}
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 手機卡片排列 */}
            <div className="md:hidden flex flex-col gap-3">
              {classes.map((classItem) => (
                <div
                  key={classItem._id}
                  className="border border-gray-300 rounded-md p-3 flex flex-col gap-2 bg-white"
                >
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500 text-sm shrink-0">書籍</span>
                    <span className="text-gray-800 text-sm text-right break-all">
                      {getBookName(classItem.bookID)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500 text-sm shrink-0">班級名稱</span>
                    <span className="text-gray-800 text-sm text-right break-all">
                      {classItem.className}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 items-center">
                    <span className="text-gray-500 text-sm shrink-0">邀請碼</span>
                    <span className="font-mono text-lg font-semibold text-gray-900">
                      {classItem.classCode}
                    </span>
                  </div>
                  <button
                    onClick={() => handleCopyInviteCode(classItem.classCode, classItem._id)}
                    className="mt-1 inline-flex items-center justify-center gap-1 px-3 py-2 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors text-sm"
                  >
                    {copiedClassId === classItem._id ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                    <span>
                      {copiedClassId === classItem._id ? '複製成功' : '複製說明'}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-center mt-4">
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              px: "32px",
              maxWidth: "120px",
            }}
          >
            關閉
          </Button>
        </div>
      </div>
    </div>
  );
};